import type { StationAlert } from '../api/types'
import { Link } from 'react-router'
import { checksStale, severity } from '../lib/health'
import { formatDateTime } from '../lib/time'
import { StationAlerts } from './StationAlerts'

/** How often Explore re-asks for a station's alerts. */
export const ALERTS_REFRESH_MS = 30_000

export interface LiveAlerts { alerts: StationAlert[]; health_checked_at: string | null }

/** The Explore page's alerts, polled every 30 s on their own by the page
 *  (which shares the poll with the header's status): the station detail is
 *  fetched once, and re-fetching it would reset the reader's selection.
 *  Says so when the health checks have stopped, so an empty space never
 *  reads as all-clear. */
export function LiveStationAlerts({ name, data }: { name: string; data: LiveAlerts | undefined }) {
  if (!data) return null
  return (
    <>
      {checksStale(data.health_checked_at) && (
        <div className="notice station-alert status-stale" role="status">
          <p className="station-alert-head">
            {data.health_checked_at
              ? `The health checks have not run since ${formatDateTime(data.health_checked_at)}.`
              : 'The health checks have not run yet.'}
          </p>
          <p className="muted">This station's alerts may not be current.</p>
        </div>
      )}
      <StationAlerts station={{ name, alerts: data.alerts }} />
    </>
  )
}

/** Compact status on reading screens; full diagnostic notices are in Sensor Info. */
export function StationStatusLink({ code, data }: { code: string; data: LiveAlerts | undefined }) {
  if (!data) return null
  const stale = checksStale(data.health_checked_at)
  if (!stale && data.alerts.length === 0) return null
  const levels = data.alerts.map(severity)
  const tone = stale ? 'stale' : levels.includes('alarm') ? 'alarm' : levels.includes('warning') ? 'warning' : 'info'
  const label = tone === 'alarm' ? 'Station alarm' : tone === 'warning' ? 'Station warning' : 'Station notices'
  return <div className="reading-status-link" role="status">
    <span className={`pill status-${tone}`}>
      {stale ? 'Health checks not current' : `${label} · ${data.alerts.length}`}
    </span>
    <Link to={`/sensors/info/${encodeURIComponent(code)}`}>View station status</Link>
  </div>
}
